import React, { memo, useState } from 'react'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { Button } from 'src/components/shared/Button'
import { FormikTextField } from '../../shared/form/formik/FormikTextField'
import { FormikTextArea } from '../../shared/form/formik/FormikTextArea'
import { Formik } from 'formik'
import { AddEditDeclineReasonFormValues } from './AddEditDeclineReasonForm.interface'
import { AddEditDeclineReasonFormSchema } from './AddEditDeclineReasonForm.validator'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { useDispatch } from 'react-redux'
import { handleApiError } from 'src/utils/errorHandlers'
import { showToast } from 'src/utils/toast'
import { CreateRejectionReason, UpdateRejectionReason } from 'src/constants/actionTypes'
import { RejectionReason } from 'src/interfaces/rejectionReason'

interface Props {
  initialReason?: RejectionReason | null
  onClose: () => void
  onSubmit: () => void
}

const initialValues: AddEditDeclineReasonFormValues = {
  name: '',
  message: '',
}

const AddEditDeclineReasonFormComponent = ({ initialReason, onClose, onSubmit }: Props) => {
  const dispatch = useDispatch()

  const isEditMode = Boolean(initialReason)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleFormSubmit = async (values: AddEditDeclineReasonFormValues) => {
    setIsLoading(true)

    try {
      if (isEditMode) {
        await createAsyncAction(
          dispatch,
          UpdateRejectionReason.request({ ...values, reasonId: initialReason?.id }),
        )

        showToast('Decline reason updated.', { variant: 'success' })
      } else {
        await createAsyncAction(dispatch, CreateRejectionReason.request(values))

        showToast('Decline reason added.', { variant: 'success' })
      }

      onSubmit()
      onClose()
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={
        initialReason ? { name: initialReason.name, message: initialReason.message } : initialValues
      }
      onSubmit={handleFormSubmit}
      validationSchema={AddEditDeclineReasonFormSchema}
    >
      {({ handleSubmit }) => (
        <div css={tw`w-[400px] bg-white flex flex-col rounded-lg shadow-lg`}>
          <ModalHeader title={isEditMode ? 'Edit reason' : 'Add new reason'} onClose={onClose} />
          <div css={tw`p-4 flex flex-col`}>
            <FormikTextField
              name="name"
              label="Decline reason name"
              css={tw`w-full mb-6`}
              placeholder="Enter reason name"
            />
            <FormikTextArea
              name="message"
              label="Contractor message"
              css={tw`w-full`}
              placeholder="Enter contractor message"
            />
          </div>
          <div
            css={tw`px-4 py-3 flex justify-between border-0 border-t border-solid border-[#EDEDED]`}
          >
            <Button
              variant="contained"
              color="grey"
              size="small"
              onClick={onClose}
              disabled={isLoading}
              className={tw`w-[100px]`}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleSubmit()}
              loading={isLoading}
              className={tw`w-[100px]`}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </Formik>
  )
}

export const AddEditDeclineReasonForm = memo(AddEditDeclineReasonFormComponent)
